/**
 * Graph Layout Utilities
 *
 * Professional-grade layout algorithms for dependency graphs, call graphs,
 * and cluster visualizations. Uses dagre for hierarchical layouts and
 * d3-force for force-directed layouts.
 */

import Dagre from '@dagrejs/dagre'
import { forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide, forceX, forceY } from 'd3-force'

// ============================================================================
// LAYOUT CONFIGURATION
// ============================================================================

export const LAYOUT_OPTIONS = {
  hierarchical: {
    label: 'Hierarchical',
    description: 'Tree-like top-down or left-right layout',
    icon: 'ListTree',
  },
  'hierarchical-lr': {
    label: 'Hierarchical (L→R)',
    description: 'Tree-like left-to-right layout',
    icon: 'ArrowRight',
  },
  force: {
    label: 'Force-Directed',
    description: 'Physics-based node positioning',
    icon: 'Share2',
  },
  radial: {
    label: 'Radial',
    description: 'Circular layout around center',
    icon: 'Circle',
  },
  grid: {
    label: 'Grid',
    description: 'Organized grid arrangement',
    icon: 'Grid3X3',
  },
  clustered: {
    label: 'Clustered',
    description: 'Group nodes by type',
    icon: 'Boxes',
  },
}

// ============================================================================
// DAGRE HIERARCHICAL LAYOUT
// ============================================================================

/**
 * Apply hierarchical layout using Dagre
 * @param {Array} nodes - Array of React Flow nodes
 * @param {Array} edges - Array of React Flow edges
 * @param {Object} options - Layout options
 * @returns {Array} - Nodes with updated positions
 */
export function applyHierarchicalLayout(nodes, edges, options = {}) {
  const {
    direction = 'TB', // TB, BT, LR, RL
    nodeWidth = 150,
    nodeHeight = 50,
    rankSep = 80,    // Vertical spacing between ranks
    nodeSep = 40,    // Horizontal spacing between nodes
    edgeSep = 20,    // Edge spacing
    align = 'UL',    // UL, UR, DL, DR
  } = options

  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))

  g.setGraph({
    rankdir: direction,
    nodesep: nodeSep,
    ranksep: rankSep,
    edgesep: edgeSep,
    align: align,
    marginx: 50,
    marginy: 50,
  })

  // Add nodes to dagre graph
  nodes.forEach((node) => {
    const width = node.measured?.width || nodeWidth
    const height = node.measured?.height || nodeHeight
    g.setNode(node.id, { width, height })
  })

  // Add edges to dagre graph
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target)
  })

  // Run layout algorithm
  Dagre.layout(g)

  // Update node positions
  return nodes.map((node) => {
    const pos = g.node(node.id)
    if (!pos) return node

    // Center the node (dagre gives center position, React Flow uses top-left)
    const width = node.measured?.width || nodeWidth
    const height = node.measured?.height || nodeHeight

    return {
      ...node,
      position: {
        x: pos.x - width / 2,
        y: pos.y - height / 2,
      },
    }
  })
}

// ============================================================================
// D3-FORCE LAYOUT
// ============================================================================

/**
 * Apply force-directed layout using d3-force
 * @param {Array} nodes - Array of React Flow nodes
 * @param {Array} edges - Array of React Flow edges
 * @param {Object} options - Layout options
 * @returns {Promise<Array>} - Nodes with updated positions
 */
export function applyForceLayout(nodes, edges, options = {}) {
  const {
    width = 1000,
    height = 800,
    linkDistance = 100,
    chargeStrength = -300,
    collisionRadius = 50,
    centerStrength = 0.1,
    iterations = 300,
  } = options

  return new Promise((resolve) => {
    // Create simulation nodes (d3 mutates these)
    const simNodes = nodes.map((node) => ({
      id: node.id,
      x: node.position?.x || Math.random() * width,
      y: node.position?.y || Math.random() * height,
      nodeType: node.data?.nodeType,
    }))

    // Create simulation links
    const simLinks = edges.map((edge) => ({
      source: edge.source,
      target: edge.target,
    }))

    // Create force simulation
    const simulation = forceSimulation(simNodes)
      .force('link', forceLink(simLinks)
        .id((d) => d.id)
        .distance(linkDistance)
      )
      .force('charge', forceManyBody().strength(chargeStrength))
      .force('center', forceCenter(width / 2, height / 2).strength(centerStrength))
      .force('collision', forceCollide().radius(collisionRadius))
      .stop()

    // Run simulation
    for (let i = 0; i < iterations; i++) {
      simulation.tick()
    }

    // Update node positions
    const nodeMap = new Map(simNodes.map((n) => [n.id, n]))
    const updatedNodes = nodes.map((node) => {
      const simNode = nodeMap.get(node.id)
      if (!simNode) return node

      return {
        ...node,
        position: {
          x: simNode.x,
          y: simNode.y,
        },
      }
    })

    resolve(updatedNodes)
  })
}

// ============================================================================
// RADIAL LAYOUT
// ============================================================================

/**
 * Apply radial/circular layout
 * @param {Array} nodes - Array of React Flow nodes
 * @param {Array} edges - Array of React Flow edges
 * @param {Object} options - Layout options
 * @returns {Array} - Nodes with updated positions
 */
export function applyRadialLayout(nodes, edges, options = {}) {
  const {
    centerX = 500,
    centerY = 400,
    radius = 300,
    startAngle = -Math.PI / 2, // Start from top
    groupByType = true,
  } = options

  if (nodes.length === 0) return nodes

  let sortedNodes = [...nodes]

  // Optionally group by type
  if (groupByType) {
    sortedNodes.sort((a, b) => {
      const typeA = a.data?.nodeType || ''
      const typeB = b.data?.nodeType || ''
      return typeA.localeCompare(typeB)
    })
  }

  // Calculate positions
  const angleStep = (2 * Math.PI) / sortedNodes.length

  return sortedNodes.map((node, index) => {
    const angle = startAngle + (index * angleStep)
    return {
      ...node,
      position: {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      },
    }
  })
}

// ============================================================================
// GRID LAYOUT
// ============================================================================

/**
 * Apply grid layout
 * @param {Array} nodes - Array of React Flow nodes
 * @param {Object} options - Layout options
 * @returns {Array} - Nodes with updated positions
 */
export function applyGridLayout(nodes, options = {}) {
  const {
    columns = Math.ceil(Math.sqrt(nodes.length)),
    nodeWidth = 150,
    nodeHeight = 60,
    gapX = 50,
    gapY = 40,
    startX = 50,
    startY = 50,
    groupByType = false,
  } = options

  let sortedNodes = [...nodes]

  if (groupByType) {
    sortedNodes.sort((a, b) => {
      const typeA = a.data?.nodeType || ''
      const typeB = b.data?.nodeType || ''
      return typeA.localeCompare(typeB)
    })
  }

  return sortedNodes.map((node, index) => {
    const col = index % columns
    const row = Math.floor(index / columns)

    return {
      ...node,
      position: {
        x: startX + col * (nodeWidth + gapX),
        y: startY + row * (nodeHeight + gapY),
      },
    }
  })
}

// ============================================================================
// CLUSTERED LAYOUT
// ============================================================================

/**
 * Apply clustered layout - groups nodes by type with force-directed positioning
 * @param {Array} nodes - Array of React Flow nodes
 * @param {Array} edges - Array of React Flow edges
 * @param {Object} options - Layout options
 * @returns {Promise<Array>} - Nodes with updated positions
 */
export function applyClusteredLayout(nodes, edges, options = {}) {
  const {
    width = 1000,
    height = 800,
    clusterPadding = 100,
    nodePadding = 30,
  } = options

  return new Promise((resolve) => {
    // Group nodes by type
    const typeGroups = {}
    nodes.forEach((node) => {
      const type = node.data?.nodeType || 'default'
      if (!typeGroups[type]) typeGroups[type] = []
      typeGroups[type].push(node)
    })

    const types = Object.keys(typeGroups)
    const clusterCenters = {}

    // Calculate cluster centers in a circle
    types.forEach((type, index) => {
      const angle = (2 * Math.PI * index) / types.length - Math.PI / 2
      const radius = Math.min(width, height) / 3
      clusterCenters[type] = {
        x: width / 2 + radius * Math.cos(angle),
        y: height / 2 + radius * Math.sin(angle),
      }
    })

    // Create simulation with cluster forces
    const simNodes = nodes.map((node) => ({
      id: node.id,
      x: node.position?.x || Math.random() * width,
      y: node.position?.y || Math.random() * height,
      nodeType: node.data?.nodeType || 'default',
    }))

    const simLinks = edges.map((edge) => ({
      source: edge.source,
      target: edge.target,
    }))

    const simulation = forceSimulation(simNodes)
      .force('link', forceLink(simLinks).id((d) => d.id).distance(80).strength(0.3))
      .force('charge', forceManyBody().strength(-150))
      .force('collision', forceCollide().radius(nodePadding))
      .force('x', forceX((d) => clusterCenters[d.nodeType]?.x || width / 2).strength(0.5))
      .force('y', forceY((d) => clusterCenters[d.nodeType]?.y || height / 2).strength(0.5))
      .stop()

    // Run simulation
    for (let i = 0; i < 300; i++) {
      simulation.tick()
    }

    // Update node positions
    const nodeMap = new Map(simNodes.map((n) => [n.id, n]))
    const updatedNodes = nodes.map((node) => {
      const simNode = nodeMap.get(node.id)
      if (!simNode) return node

      return {
        ...node,
        position: {
          x: simNode.x,
          y: simNode.y,
        },
      }
    })

    resolve(updatedNodes)
  })
}

// ============================================================================
// MAIN LAYOUT FUNCTION
// ============================================================================

/**
 * Apply layout to nodes based on layout type
 * @param {string} layoutType - Type of layout to apply
 * @param {Array} nodes - Array of React Flow nodes
 * @param {Array} edges - Array of React Flow edges
 * @param {Object} options - Layout-specific options
 * @returns {Promise<Array>} - Nodes with updated positions
 */
export async function applyLayout(layoutType, nodes, edges, options = {}) {
  if (!nodes || nodes.length === 0) return nodes

  switch (layoutType) {
    case 'hierarchical':
      return applyHierarchicalLayout(nodes, edges, { direction: 'TB', ...options })

    case 'hierarchical-lr':
      return applyHierarchicalLayout(nodes, edges, { direction: 'LR', ...options })

    case 'hierarchical-bt':
      return applyHierarchicalLayout(nodes, edges, { direction: 'BT', ...options })

    case 'hierarchical-rl':
      return applyHierarchicalLayout(nodes, edges, { direction: 'RL', ...options })

    case 'force':
      return applyForceLayout(nodes, edges, options)

    case 'radial':
      return applyRadialLayout(nodes, edges, options)

    case 'grid':
      return applyGridLayout(nodes, options)

    case 'clustered':
      return applyClusteredLayout(nodes, edges, options)

    default:
      return nodes
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get bounding box of nodes
 */
export function getNodesBounds(nodes) {
  if (!nodes || nodes.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  nodes.forEach((node) => {
    const x = node.position?.x || 0
    const y = node.position?.y || 0
    const width = node.measured?.width || 150
    const height = node.measured?.height || 50

    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x + width)
    maxY = Math.max(maxY, y + height)
  })

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  }
}

/**
 * Center nodes in viewport
 */
export function centerNodes(nodes, viewportWidth = 1000, viewportHeight = 800) {
  const bounds = getNodesBounds(nodes)
  const offsetX = (viewportWidth - bounds.width) / 2 - bounds.x
  const offsetY = (viewportHeight - bounds.height) / 2 - bounds.y

  return nodes.map((node) => ({
    ...node,
    position: {
      x: (node.position?.x || 0) + offsetX,
      y: (node.position?.y || 0) + offsetY,
    },
  }))
}

/**
 * Find root nodes (nodes with no incoming edges)
 */
export function findRootNodes(nodes, edges) {
  const targetIds = new Set(edges.map((e) => e.target))
  return nodes.filter((node) => !targetIds.has(node.id))
}

/**
 * Find leaf nodes (nodes with no outgoing edges)
 */
export function findLeafNodes(nodes, edges) {
  const sourceIds = new Set(edges.map((e) => e.source))
  return nodes.filter((node) => !sourceIds.has(node.id))
}

/**
 * Calculate node depth from root
 */
export function calculateNodeDepths(nodes, edges) {
  const depths = {}
  const adjacency = {}

  // Build adjacency list
  edges.forEach((edge) => {
    if (!adjacency[edge.source]) adjacency[edge.source] = []
    adjacency[edge.source].push(edge.target)
  })

  // Find roots
  const roots = findRootNodes(nodes, edges)

  // BFS to calculate depths
  const queue = roots.map((node) => ({ id: node.id, depth: 0 }))
  const visited = new Set()

  while (queue.length > 0) {
    const { id, depth } = queue.shift()
    if (visited.has(id)) continue
    visited.add(id)
    depths[id] = depth

    const children = adjacency[id] || []
    children.forEach((childId) => {
      if (!visited.has(childId)) {
        queue.push({ id: childId, depth: depth + 1 })
      }
    })
  }

  // Handle disconnected nodes
  nodes.forEach((node) => {
    if (depths[node.id] === undefined) {
      depths[node.id] = 0
    }
  })

  return depths
}
